import React, { useEffect, useState } from "react"
import styles from "./platformUpdates.module.css"
import api from "../../services/api"
import Button from "../../components/Form/Button"
import Input from "../../components/Form/Input"
import Textarea from "../../components/Form/Textarea"
import Select from "../../components/Form/Select"
import ColorPicker from "../../components/Form/ColorPicker"
import Checkbox from "../../components/Form/Checkbox"
import FormGroup from "../../components/Form/FormGroup"
import InputFile from "../../components/Form/InputFile"
import Modal from "../../components/Modal"
import ModalDelete from "../../components/Modal/ModalDelete"
import WhatsNewModal from "../../components/WhatsNewModal"
import Loading from "../layouts/Loading"
import { toast } from "react-toastify"
import { MdAutoAwesome, MdOutlineNewspaper, MdClose, MdOutlineAdd, MdOutlineBuild, MdOutlineDeleteOutline, MdOutlineFileDownload, MdOutlineFileUpload, MdOutlineRocketLaunch, MdOutlineSettingsSuggest, MdOutlineSyncAlt, MdOutlineVisibility } from "react-icons/md"

const MAX_IMPORT_FILE_SIZE = 5 * 1024 * 1024

const formatUpdateDate = (date) => {
    if (!date) return ""
    return new Date(date).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    })
}

const categories = [
    { id: "new", value: "Novo recurso", icon: <MdOutlineRocketLaunch /> },
    { id: "improvement", value: "Melhoria", icon: <MdAutoAwesome /> },
    { id: "fix", value: "Correção", icon: <MdOutlineBuild /> },
    { id: "automation", value: "Automação", icon: <MdOutlineSettingsSuggest /> },
    { id: "integration", value: "Integração", icon: <MdOutlineSyncAlt /> },
]

const impacts = [
    { id: "informative", value: "Informativo" },
    { id: "requires_configuration", value: "Requer configuração" },
    { id: "automatic", value: "Disponível automaticamente" },
    { id: "new_feature", value: "Nova funcionalidade" },
    { id: "fix", value: "Correção" },
]

const linkTypes = [
    { id: "internal", value: "Rota interna" },
    { id: "external", value: "Link externo" },
]

const audienceVisibilityOptions = [
    { id: "all", value: "Todos os usuários" },
    { id: "account_admins", value: "Somente administradores da conta" },
]

const permissionOptions = [
    { id: "crm", value: "Kanban" },
    { id: "dashboard", value: "Dashboard" },
    { id: "chat", value: "Chat" },
    { id: "tasks", value: "Tarefas" },
    { id: "notes", value: "Anotações" },
    { id: "reports", value: "Relatórios" },
    { id: "integrations", value: "Integrações" },
    { id: "settings", value: "Configurações" },
    { id: "manage_users", value: "Gerenciar usuários" },
    { id: "finance", value: "Financeiro" },
]

const emptyForm = {
    title: "",
    summary: "",
    details: "",
    version: "",
    category: "improvement",
    impact: "informative",
    image_url: "",
    accent_color: "#6245ae",
    published_at: new Date().toISOString().slice(0, 10),
    is_published: true,
    primary_action: { label: "", url: "", type: "internal" },
    links: [],
    audience: { visibility: "all", modules: [], permissions: [] },
    internal_notes: "",
    highlights: [{ title: "", description: "", icon: "improvement" }],
}

const PlatformUpdates = () => {
    const [updates, setUpdates] = useState([])
    const [canManage, setCanManage] = useState(false)
    const [moduleOptions, setModuleOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [showDelete, setShowDelete] = useState(false)
    const [form, setForm] = useState(emptyForm)
    const [imageFile, setImageFile] = useState([])
    const [deleteId, setDeleteId] = useState("")
    const [previewUpdate, setPreviewUpdate] = useState(null)
    const [exportItems, setExportItems] = useState(null)
    const [selectedExportIds, setSelectedExportIds] = useState([])
    const [exportPreviewId, setExportPreviewId] = useState("")
    const [importPreview, setImportPreview] = useState(null)
    const [selectedImportIds, setSelectedImportIds] = useState([])
    const [importPreviewId, setImportPreviewId] = useState("")

    useEffect(() => {
        getUpdates()
    }, [])

    useEffect(() => {
        if (canManage) {
            getModules()
        }
    }, [canManage])

    const getUpdates = async () => {
        try {
            setLoading(true)
            const response = await api.get("/platform-updates")
            setUpdates(response?.data?.data || [])
            setCanManage(Boolean(response?.data?.can_manage))
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setLoading(false)
        }
    }

    const getModules = async () => {
        try {
            const response = await api.get("/modules")
            const modules = response?.data?.data || []
            setModuleOptions(modules.map(module => ({ id: module.action, value: module.name })))
        } catch (err) {
            toast.error(err?.response?.data?.message)
        }
    }

    const uploadPlatformUpdateImage = async (file) => {
        const formData = new FormData()
        formData.append("file", file)

        const response = await api.post("/platform-updates/images", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        })

        return response?.data?.data
    }

    const openNewForm = () => {
        setForm(emptyForm)
        setImageFile([])
        setShowForm(true)
    }

    const openEditForm = (update) => {
        setForm({
            ...emptyForm,
            ...update,
            primary_action: { ...emptyForm.primary_action, ...(update?.primary_action || {}) },
            audience: { ...emptyForm.audience, ...(update?.audience || {}) },
            links: Array.isArray(update?.links) ? update.links : [],
            published_at: update?.published_at ? update.published_at.slice(0, 10) : emptyForm.published_at,
            highlights: update?.highlights?.length > 0 ? update.highlights : emptyForm.highlights,
        })
        setImageFile([])
        setShowForm(true)
    }

    const closeForm = () => {
        setShowForm(false)
        setForm(emptyForm)
        setImageFile([])
    }

    const handleChange = (field, value) => {
        setForm(previous => ({ ...previous, [field]: value }))
    }

    const handlePrimaryActionChange = (field, value) => {
        setForm(previous => ({
            ...previous,
            primary_action: { ...previous.primary_action, [field]: value },
        }))
    }

    const handleAudienceChange = (field, value) => {
        setForm(previous => ({
            ...previous,
            audience: { ...previous.audience, [field]: value },
        }))
    }

    const handleLinkChange = (index, field, value) => {
        setForm(previous => {
            const links = [...previous.links]
            links[index] = { ...links[index], [field]: value }
            return { ...previous, links }
        })
    }

    const addLink = () => {
        setForm(previous => ({
            ...previous,
            links: [...previous.links, { label: "", url: "", type: "internal" }],
        }))
    }

    const removeLink = (index) => {
        setForm(previous => ({
            ...previous,
            links: previous.links.filter((_, itemIndex) => itemIndex !== index),
        }))
    }

    const handleHighlightChange = (index, field, value) => {
        setForm(previous => {
            const highlights = [...previous.highlights]
            highlights[index] = { ...highlights[index], [field]: value }
            return { ...previous, highlights }
        })
    }

    const addHighlight = () => {
        setForm(previous => ({
            ...previous,
            highlights: [...previous.highlights, { title: "", description: "", icon: previous.category }],
        }))
    }

    const removeHighlight = (index) => {
        setForm(previous => ({
            ...previous,
            highlights: previous.highlights.filter((_, itemIndex) => itemIndex !== index),
        }))
    }

    const saveUpdate = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            let imageUrl = form.image_url

            if (imageFile?.[0]) {
                const uploadedImage = await uploadPlatformUpdateImage(imageFile[0])
                imageUrl = uploadedImage?.url || ""
                if (!imageUrl) {
                    throw new Error("Não foi possível obter a URL da imagem enviada.")
                }
            }

            const payload = {
                ...form,
                image_url: imageUrl,
                highlights: form.highlights.filter(highlight => highlight.title?.trim()),
            }
            if (form?._id) {
                const response = await api.patch(`/platform-updates/${form._id}`, payload)
                toast.success(response?.data?.message)
            } else {
                const response = await api.post("/platform-updates", payload)
                toast.success(response?.data?.message)
            }
            closeForm()
            getUpdates()
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message)
        } finally {
            setLoading(false)
        }
    }

    const removeImage = () => {
        setImageFile([])
        handleChange("image_url", "")
    }

    const openDelete = (id) => {
        setDeleteId(id)
        setShowDelete(true)
    }

    const deleteUpdate = async (id) => {
        try {
            setLoading(true)
            const response = await api.delete(`/platform-updates/${id}`)
            setUpdates(previous => previous.filter(update => update._id !== id))
            toast.success(response?.data?.message)
            setShowDelete(false)
        } catch (err) {
            toast.error(err?.response?.data?.message)
        } finally {
            setLoading(false)
        }
    }

    const closeExportModal = () => {
        setExportItems(null)
        setSelectedExportIds([])
        setExportPreviewId("")
    }

    const openExportModal = () => {
        if (!updates.length) return toast.error("Nenhuma novidade disponível para exportar.")
        setExportItems(updates)
        setSelectedExportIds(updates.map(update => update._id))
        setExportPreviewId(updates[0]?._id || "")
    }

    const toggleExportSelection = (id) => {
        setSelectedExportIds(previous => previous.includes(id) ? previous.filter(item => item !== id) : [...previous, id])
    }

    const toggleAllExportItems = () => {
        const ids = (exportItems || []).map(item => item._id)
        setSelectedExportIds(previous => ids.length && ids.every(id => previous.includes(id)) ? [] : ids)
    }

    const exportUpdates = async () => {
        if (!selectedExportIds.length) return toast.error("Selecione pelo menos uma novidade para exportar.")
        try {
            setLoading(true)
            const response = await api.post("/platform-updates/export", { update_ids: selectedExportIds }, { responseType: "blob" })
            const disposition = response.headers?.["content-disposition"] || ""
            const fileName = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `novidades-oktto-${new Date().toISOString().slice(0, 10)}.json`
            const url = URL.createObjectURL(response.data)
            const link = document.createElement("a")
            link.href = url
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
            toast.success(`${selectedExportIds.length} novidade(s) exportada(s) com sucesso!`)
            closeExportModal()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível exportar as novidades.")
        } finally {
            setLoading(false)
        }
    }

    const selectImportFile = async (files) => {
        const file = files?.[0]
        if (!file) return
        if (file.size > MAX_IMPORT_FILE_SIZE) return toast.error("O arquivo deve ter no máximo 5 MB.")
        try {
            setLoading(true)
            const payload = JSON.parse(await file.text())
            const response = await api.post("/platform-updates/import-preview", payload)
            const data = response?.data?.data || {}
            const items = data.items || []
            const validIds = items.filter(item => item.valid).map(item => item.import_id)
            setImportPreview({ fileName: file.name, ...data, items })
            setSelectedImportIds(validIds)
            setImportPreviewId(items.find(item => item.valid)?.import_id || items[0]?.import_id || "")
        } catch (err) {
            toast.error(err instanceof SyntaxError ? "O arquivo selecionado não contém um JSON válido." : (err?.response?.data?.message || "Não foi possível validar o arquivo de novidades."))
        } finally {
            setLoading(false)
        }
    }

    const closeImportModal = () => {
        setImportPreview(null)
        setSelectedImportIds([])
        setImportPreviewId("")
    }

    const toggleImportSelection = (item) => {
        if (!item?.valid) return
        setSelectedImportIds(previous => previous.includes(item.import_id) ? previous.filter(id => id !== item.import_id) : [...previous, item.import_id])
    }

    const toggleAllValidImportItems = () => {
        const validIds = (importPreview?.items || []).filter(item => item.valid).map(item => item.import_id)
        setSelectedImportIds(previous => validIds.length && validIds.every(id => previous.includes(id)) ? [] : validIds)
    }

    const importUpdates = async () => {
        const updatesToImport = (importPreview?.items || [])
            .filter(item => item.valid && selectedImportIds.includes(item.import_id))
            .map(item => item.update)
        if (!updatesToImport.length) return toast.error("Selecione pelo menos uma novidade válida para importar.")
        try {
            setLoading(true)
            const response = await api.post("/platform-updates/import", { format: "oktto-platform-updates", version: 1, platform_updates: updatesToImport })
            toast.success(response?.data?.message)
            closeImportModal()
            await getUpdates()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível importar as novidades.")
        } finally {
            setLoading(false)
        }
    }

    const getTransferStatus = (update) => {
        if (!update?.is_published) return "Rascunho"
        if (new Date(update.published_at) > new Date()) return "Agendada"
        return "Publicada"
    }

    const getCategory = (categoryId) => categories.find(category => category.id === categoryId) || categories[1]
    const previewIndex = previewUpdate ? updates.findIndex(update => update._id === previewUpdate._id) : -1
    const showPreviousPreview = previewIndex > 0
    const showNextPreview = previewIndex !== -1 && previewIndex < updates.length - 1
    const selectedExportPreview = (exportItems || []).find(item => item._id === exportPreviewId) || exportItems?.[0]
    const selectedImportPreview = (importPreview?.items || []).find(item => item.import_id === importPreviewId) || importPreview?.items?.[0]

    const showPreviousUpdate = () => {
        if (showPreviousPreview) {
            setPreviewUpdate(updates[previewIndex - 1])
        }
    }

    const showNextUpdate = () => {
        if (showNextPreview) {
            setPreviewUpdate(updates[previewIndex + 1])
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.topbar}>
                <div className={styles.intro}>
                    <strong>Novidades da Oktto</strong>
                    <span>Acompanhe melhorias, correções e recursos que chegaram na plataforma.</span>
                </div>
                {canManage &&
                    <div className={styles.topbarActions}>
                        <Button variant="secondary" fullWidth={false} onClick={openExportModal}><MdOutlineFileDownload /> Exportar JSON</Button>
                        <InputFile value={[]} accept="application/json,.json" onChange={selectImportFile} className={styles.importFile} buttonClassName={styles.importFileButton} buttonVariant="secondary" buttonContent={<><MdOutlineFileUpload /> Importar JSON</>} />
                        <Button variant="primary" fullWidth={false} onClick={openNewForm}><MdOutlineAdd /> Adicionar novidade</Button>
                    </div>
                }
            </div>

            {updates.length > 0 ?
                <div className={styles.grid}>
                    {updates.map((update) => {
                        const category = getCategory(update.category)
                        const accentColor = update?.accent_color || "#6245ae"
                        const isScheduled = update?.is_published && new Date(update.published_at) > new Date()
                        return (
                            <article className={styles.card} key={update._id} style={{ "--accent-color": accentColor }}>
                                <div className={styles.cardHeader}>
                                    <span className={styles.badge}>{category.icon}{category.value}</span>
                                    <div className={styles.cardStatus}>
                                        {canManage && !update.is_published && <span className={`${styles.status} ${styles.unpublished}`}>Rascunho</span>}
                                        {canManage && isScheduled && <span className={`${styles.status} ${styles.scheduled}`}>Agendada</span>}
                                        {canManage && update.is_published && !isScheduled && <span className={`${styles.status} ${styles.published}`}>Publicada</span>}
                                        <span className={styles.datePill}>{formatUpdateDate(update.published_at)}</span>
                                    </div>
                                </div>
                                <div className={styles.text}>
                                    <h3>{update.title}</h3>
                                    <p>{update.summary}</p>
                                </div>
                                <div className={styles.meta}>
                                    <span>Versão {update.version}</span>
                                </div>
                                {update?.highlights?.length > 0 &&
                                    <div className={styles.highlights}>
                                        {update.highlights.slice(0, 3).map((highlight, index) => (
                                            <div className={styles.highlight} key={`${update._id}-${index}`}>
                                                <span className={styles.highlightIcon}>{getCategory(highlight.icon).icon}</span>
                                                <span>
                                                    <strong>{highlight.title}</strong>
                                                    {highlight.description && <span>{highlight.description}</span>}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                }
                                <div className={styles.actions}>
                                    <Button variant="neutral" className={`${styles.detailsButton}`} onClick={() => setPreviewUpdate(update)}>
                                        <MdOutlineVisibility />
                                        Ver detalhes
                                    </Button>
                                    {canManage &&
                                        <>
                                        <Button variant="neutral" onClick={() => openEditForm(update)}>Editar</Button>
                                        <Button variant="danger" onClick={() => openDelete(update._id)}>Excluir</Button>
                                        </>
                                    }
                                </div>
                            </article>
                        )
                    })}
                </div>
                :
                <div className={styles.empty}>Nenhuma novidade publicada ainda.</div>
            }

            {exportItems &&
                <Modal icon={<MdOutlineFileDownload />} title="Exportar novidades" description="Selecione quais novidades devem fazer parte do arquivo JSON." closeModal={closeExportModal} fitContent={true} heightFitContent={true} bodyClassName={styles.transferModalBody}>
                    <div className={styles.transferModal}>
                        <div className={styles.transferConfig}>
                            <div className={styles.transferSummary}>
                                <div><span>Disponíveis</span><strong>{exportItems.length}</strong></div>
                                <div><span>Selecionadas</span><strong>{selectedExportIds.length}</strong></div>
                            </div>
                            <div className={styles.transferListHeader}>
                                <button type="button" onClick={toggleAllExportItems}>{exportItems.length > 0 && exportItems.every(item => selectedExportIds.includes(item._id)) ? "Desmarcar todas" : "Selecionar todas"}</button>
                                <span>O arquivo preserva conteúdo, visual, ações e público.</span>
                            </div>
                            <div className={styles.transferList}>
                                {exportItems.map(item => <button key={item._id} type="button" className={exportPreviewId === item._id ? styles.active : ""} onClick={() => setExportPreviewId(item._id)}>
                                    <span className={`${styles.selectionBox} ${selectedExportIds.includes(item._id) ? styles.checked : ""}`} onClick={event => { event.stopPropagation(); toggleExportSelection(item._id) }} aria-hidden="true" />
                                    <div><strong>{item.title}</strong><span>{getTransferStatus(item)} · Versão {item.version}</span></div>
                                </button>)}
                            </div>
                        </div>
                        <div className={styles.transferPreview}>
                            {selectedExportPreview ? <>
                                <div className={styles.transferPreviewHeader}><span>{getTransferStatus(selectedExportPreview)}</span><strong>{selectedExportPreview.title}</strong><small>{selectedExportPreview.summary}</small></div>
                                {selectedExportPreview.details && <p className={styles.transferPreviewContent}>{selectedExportPreview.details}</p>}
                                {selectedExportPreview.highlights?.length > 0 && <div className={styles.transferPreviewHighlights}>{selectedExportPreview.highlights.map((highlight, index) => <div key={`${highlight.title}-${index}`}><strong>{highlight.title}</strong>{highlight.description && <span>{highlight.description}</span>}</div>)}</div>}
                            </> : <span className={styles.transferEmpty}>Selecione uma novidade para visualizar.</span>}
                        </div>
                        <div className={styles.transferActions}>
                            <Button variant="secondary" type="button" fullWidth={false} onClick={closeExportModal}>Cancelar</Button>
                            <Button variant="primary" type="button" fullWidth={false} onClick={exportUpdates} disabled={!selectedExportIds.length}><MdOutlineFileDownload /> Exportar {selectedExportIds.length || ""}</Button>
                        </div>
                    </div>
                </Modal>
            }
            {importPreview &&
                <Modal icon={<MdOutlineFileUpload />} title="Importar novidades" description={importPreview.fileName || "Revise as novidades antes de importar"} closeModal={closeImportModal} fitContent={true} heightFitContent={true} bodyClassName={styles.transferModalBody}>
                    <div className={styles.transferModal}>
                        <div className={styles.transferConfig}>
                            <div className={`${styles.transferSummary} ${styles.importSummary}`}>
                                <div><span>No arquivo</span><strong>{importPreview.total_count || 0}</strong></div>
                                <div><span>Válidas</span><strong>{importPreview.valid_count || 0}</strong></div>
                                <div><span>Com problemas</span><strong>{importPreview.invalid_count || 0}</strong></div>
                                <div><span>Selecionadas</span><strong>{selectedImportIds.length}</strong></div>
                            </div>
                            <div className={styles.transferListHeader}>
                                <button type="button" onClick={toggleAllValidImportItems}>{importPreview.valid_count > 0 && importPreview.items.filter(item => item.valid).every(item => selectedImportIds.includes(item.import_id)) ? "Desmarcar válidas" : "Selecionar válidas"}</button>
                                <span>Itens com problemas ficam bloqueados até o arquivo ser corrigido.</span>
                            </div>
                            <div className={styles.transferList}>
                                {importPreview.items.map(item => <button key={item.import_id} type="button" className={`${importPreviewId === item.import_id ? styles.active : ""} ${!item.valid ? styles.invalid : ""}`} onClick={() => setImportPreviewId(item.import_id)}>
                                    <span className={`${styles.selectionBox} ${selectedImportIds.includes(item.import_id) ? styles.checked : ""} ${!item.valid ? styles.disabled : ""}`} onClick={event => { event.stopPropagation(); toggleImportSelection(item) }} aria-hidden="true" />
                                    <div><strong>{item.update?.title || `Novidade ${item.position}`}</strong><span>{item.valid ? `Versão ${item.update?.version || "-"} · Pronta para importar` : item.error}</span></div>
                                    <small className={item.valid ? styles.validBadge : styles.invalidBadge}>{item.valid ? "Válida" : "Revisar"}</small>
                                </button>)}
                            </div>
                        </div>
                        <div className={styles.transferPreview}>
                            {selectedImportPreview ? <>
                                <div className={styles.transferPreviewHeader}>
                                    <span className={selectedImportPreview.valid ? styles.validText : styles.invalidText}>{selectedImportPreview.valid ? "Pronta para importar" : "Não será importada"}</span>
                                    <strong>{selectedImportPreview.update?.title || `Novidade ${selectedImportPreview.position}`}</strong>
                                    {selectedImportPreview.error && <small>{selectedImportPreview.error}</small>}
                                    {selectedImportPreview.update?.summary && <small>{selectedImportPreview.update.summary}</small>}
                                </div>
                                {selectedImportPreview.update?.details && <p className={styles.transferPreviewContent}>{selectedImportPreview.update.details}</p>}
                                {selectedImportPreview.update?.highlights?.length > 0 && <div className={styles.transferPreviewHighlights}>{selectedImportPreview.update.highlights.map((highlight, index) => <div key={`${highlight.title}-${index}`}><strong>{highlight.title}</strong>{highlight.description && <span>{highlight.description}</span>}</div>)}</div>}
                            </> : <span className={styles.transferEmpty}>Nenhuma novidade disponível para visualizar.</span>}
                        </div>
                        <div className={styles.transferNotice}>Somente as novidades válidas selecionadas serão criadas. Todas entrarão como novos rascunhos e nenhum usuário será notificado.</div>
                        <div className={styles.transferActions}>
                            <Button variant="secondary" type="button" fullWidth={false} onClick={closeImportModal}>Cancelar</Button>
                            <Button variant="primary" type="button" fullWidth={false} onClick={importUpdates} disabled={!selectedImportIds.length}><MdOutlineFileUpload /> Importar {selectedImportIds.length || ""} como rascunho</Button>
                        </div>
                    </div>
                </Modal>
            }
            {showForm &&
                <Modal
                    icon={<MdOutlineNewspaper />}
                    title={form?._id ? "Editar novidade" : "Adicionar novidade"}
                    description="Crie uma atualização curta, visual e fácil de consumir."
                    closeModal={closeForm}
                    fullWidith={true}
                    bodyClassName={styles.updateModalBody}
                >
                    <form className={styles.form} onSubmit={saveUpdate}>
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <strong>Conteúdo</strong>
                                <span>Informações principais que o cliente verá no card e no modal.</span>
                            </div>
                        <div className={styles.formGrid}>
                            <FormGroup labelText="Título">
                                <Input value={form.title} onChange={(e) => handleChange("title", e.target.value)} placeholder="Título" required />
                            </FormGroup>
                            <FormGroup labelText="Número da versão">
                                <Input value={form.version} onChange={(e) => handleChange("version", e.target.value)} placeholder="Número da versão" required />
                            </FormGroup>
                            <FormGroup labelText="Categoria">
                                <Select
                                    name="Categoria"
                                    placeholder="Categoria"
                                    options={categories}
                                    selected={form.category}
                                    onChange={(value) => handleChange("category", value || "improvement")}
                                    showOptionAll={false}
                                    allowDeselectAll={false}
                                />
                            </FormGroup>
                            <FormGroup labelText="Impacto">
                                <Select
                                    name="Impacto"
                                    placeholder="Impacto"
                                    options={impacts}
                                    selected={form.impact}
                                    onChange={(value) => handleChange("impact", value || "informative")}
                                    showOptionAll={false}
                                    allowDeselectAll={false}
                                />
                            </FormGroup>
                            <FormGroup labelText="Data de publicação">
                                <Input type="date" value={form.published_at} onChange={(e) => handleChange("published_at", e.target.value)} placeholder="Data de publicação" required />
                            </FormGroup>
                        </div>
                        <FormGroup labelText="Resumo">
                            <Textarea rows={3} value={form.summary} onChange={(e) => handleChange("summary", e.target.value)} placeholder="Resumo" required />
                        </FormGroup>
                        <FormGroup labelText="Descrição detalhada">
                            <Textarea rows={5} value={form.details} onChange={(e) => handleChange("details", e.target.value)} placeholder="Descrição detalhada" />
                        </FormGroup>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <strong>Visual</strong>
                                <span>Imagem e cor usadas para dar identidade à atualização.</span>
                            </div>
                            <div className={styles.formGrid}>
                            <FormGroup labelText="Cor de destaque">
                                <ColorPicker
                                    value={form.accent_color}
                                    onChange={(value) => handleChange("accent_color", value)}
                                    label="Cor da novidade"
                                />
                            </FormGroup>
                            <button
                                type="button"
                                className={styles.checkField}
                                onClick={() => handleChange("is_published", !form.is_published)}
                            >
                                <Checkbox checked={form.is_published} pink={true} />
                                <span>Publicada</span>
                            </button>
                        </div>
                        <FormGroup labelText="Imagem opcional">
                            <div className={styles.imageUpload}>
                                {form.image_url && !imageFile?.[0] &&
                                    <div className={styles.imagePreview}>
                                        <img src={form.image_url} alt="Imagem da novidade" />
                                        <Button variant="neutral" type="button" className={`${styles.fitButton}`} onClick={removeImage}>Remover imagem</Button>
                                    </div>
                                }
                                <InputFile
                                    value={imageFile}
                                    onChange={setImageFile}
                                    accept="image/png,image/jpeg,image/jpg,image/webp"
                                    multiple={false}
                                    placeholder="Imagem opcional"
                                />
                            </div>
                        </FormGroup>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <strong>Ações</strong>
                                <span>Botões e links úteis exibidos no modal da novidade.</span>
                            </div>
                            <div className={styles.formGrid}>
                                <FormGroup labelText="Texto do CTA principal">
                                    <Input value={form.primary_action?.label || ""} onChange={(e) => handlePrimaryActionChange("label", e.target.value)} placeholder="Texto do CTA principal" />
                                </FormGroup>
                                <FormGroup labelText="Destino do CTA principal">
                                    <Input value={form.primary_action?.url || ""} onChange={(e) => handlePrimaryActionChange("url", e.target.value)} placeholder="Destino do CTA principal" />
                                </FormGroup>
                                <FormGroup labelText="Tipo do CTA principal">
                                    <Select
                                        name="Tipo do CTA principal"
                                        placeholder="Tipo do CTA principal"
                                        options={linkTypes}
                                        selected={form.primary_action?.type || "internal"}
                                        onChange={(value) => handlePrimaryActionChange("type", value || "internal")}
                                        showOptionAll={false}
                                        allowDeselectAll={false}
                                    />
                                </FormGroup>
                            </div>
                            <div className={styles.linkEditor}>
                                <div className={styles.topbar}>
                                    <div className={styles.intro}>
                                        <strong>Links adicionais</strong>
                                        <span>Adicione documentação, configuração ou suporte quando fizer sentido.</span>
                                    </div>
                                    <Button variant="neutral" type="button" className={`${styles.fitButton}`} onClick={addLink}>Adicionar link</Button>
                                </div>
                                {form.links.map((link, index) => (
                                    <div className={styles.linkRow} key={index}>
                                        <Input value={link.label} onChange={(e) => handleLinkChange(index, "label", e.target.value)} placeholder="Texto do link" />
                                        <Input value={link.url} onChange={(e) => handleLinkChange(index, "url", e.target.value)} placeholder="Destino do link" />
                                        <Select
                                            name="Tipo"
                                            placeholder="Tipo"
                                            options={linkTypes}
                                            selected={link.type || "internal"}
                                            onChange={(value) => handleLinkChange(index, "type", value || "internal")}
                                            showOptionAll={false}
                                            allowDeselectAll={false}
                                        />
                                        <Button variant="neutral" type="button" className="action" onClick={() => removeLink(index)}><MdClose /></Button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <strong>Destaques</strong>
                                <span>Use frases curtas para transformar texto em cards.</span>
                            </div>
                        <div className={styles.highlightEditor}>
                            <div className={styles.topbar}>
                                <div className={styles.intro}>
                                    <strong>Destaques</strong>
                                    <span>Itens exibidos como blocos visuais no modal.</span>
                                </div>
                                <Button variant="neutral" type="button" className={`${styles.fitButton}`} onClick={addHighlight}>Adicionar destaque</Button>
                            </div>
                            {form.highlights.map((highlight, index) => (
                                <div className={styles.highlightRow} key={index}>
                                    <Input value={highlight.title} onChange={(e) => handleHighlightChange(index, "title", e.target.value)} placeholder="Título" />
                                    <Input value={highlight.description} onChange={(e) => handleHighlightChange(index, "description", e.target.value)} placeholder="Descrição curta" />
                                    <Select
                                        name="Ícone"
                                        placeholder="Ícone"
                                        options={categories}
                                        selected={highlight.icon || form.category}
                                        onChange={(value) => handleHighlightChange(index, "icon", value || form.category)}
                                        showOptionAll={false}
                                        allowDeselectAll={false}
                                    />
                                    <Button variant="neutral" type="button" className="action" onClick={() => removeHighlight(index)}><MdClose /></Button>
                                </div>
                            ))}
                        </div>
                        </div>

                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <strong>Exibição</strong>
                                <span>Defina quem deve receber essa comunicação. Módulos e permissões são filtros opcionais.</span>
                            </div>
                            <div className={styles.formGrid}>
                                <FormGroup labelText="Público">
                                    <Select
                                        name="Público"
                                        placeholder="Público"
                                        options={audienceVisibilityOptions}
                                        selected={form.audience?.visibility || "all"}
                                        onChange={(value) => handleAudienceChange("visibility", value || "all")}
                                        showOptionAll={false}
                                        allowDeselectAll={false}
                                    />
                                </FormGroup>
                                <FormGroup labelText="Módulos">
                                    <Select
                                        name="Módulos"
                                        placeholder="Módulos"
                                        options={moduleOptions}
                                        selected={form.audience?.modules || []}
                                        onChange={(value) => handleAudienceChange("modules", value || [])}
                                        multiple={true}
                                        showOptionAll={false}
                                    />
                                </FormGroup>
                                <FormGroup labelText="Permissões">
                                    <Select
                                        name="Permissões"
                                        placeholder="Permissões"
                                        options={permissionOptions}
                                        selected={form.audience?.permissions || []}
                                        onChange={(value) => handleAudienceChange("permissions", value || [])}
                                        multiple={true}
                                        showOptionAll={false}
                                    />
                                </FormGroup>
                            </div>
                            <FormGroup labelText="Observações internas">
                                <Textarea rows={3} value={form.internal_notes} onChange={(e) => handleChange("internal_notes", e.target.value)} placeholder="Observações internas" />
                            </FormGroup>
                        </div>
                        <div className={styles.formActions}>
                            <Button variant="neutral" type="button" className={`${styles.fitButton}`} onClick={closeForm}>Cancelar</Button>
                            <Button variant="primary" type="submit" className={`${styles.fitButton}`}>Salvar</Button>
                        </div>
                    </form>
                </Modal>
            }
            {showDelete &&
                <ModalDelete
                    closeModal={() => setShowDelete(false)}
                    onSubmit={deleteUpdate}
                    icon={<MdOutlineDeleteOutline />}
                    title="Excluir novidade"
                    description="Deseja realmente excluir esta novidade?"
                    id={deleteId}
                />
            }
            {previewUpdate &&
                <WhatsNewModal
                    update={previewUpdate}
                    onClose={() => setPreviewUpdate(null)}
                    onViewAll={() => setPreviewUpdate(null)}
                    showViewAll={false}
                    onPrevious={showPreviousUpdate}
                    onNext={showNextUpdate}
                    hasPrevious={showPreviousPreview}
                    hasNext={showNextPreview}
                />
            }
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default PlatformUpdates
